import React, { useRef, useEffect, useState } from "react";
import useFetch from "../../hooks/useFetch";
import Pagination from "../../components/Table/pagination";
import axios from "axios";
import useAuth from "../../hooks/useAuth";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ReactComponent as Edit } from "../../assets/images/svg/Edit.svg";
import { ReactComponent as Delete } from "../../assets/images/svg/Delete.svg";
import Swal from "sweetalert2";
import UseAxiosPrivate from "../../hooks/useAxiosPrivate";
import { AGENT_ACTIVITY } from "../../utils/ApiUrls";

const API = process.env.REACT_APP_API_URL;

// adminCanSet: false → driven by the call flow, shown but not selectable
const STATUS_OPTIONS = [
  { value: "available", label: "Available", bg: "bg-green-50", text: "text-green-600", dot: "bg-green-600", adminCanSet: true },
  { value: "incall", label: "Incall", bg: "bg-orange-50", text: "text-orange-600", dot: "bg-orange-600", adminCanSet: false },
  { value: "waiting", label: "Waiting", bg: "bg-pink-50", text: "text-pink-600", dot: "bg-pink-600", adminCanSet: false },
  { value: "break", label: "Break", bg: "bg-blue-50", text: "text-blue-600", dot: "bg-blue-600", adminCanSet: true },
  { value: "unavailable", label: "Unavailable", bg: "bg-red-50", text: "text-red-600", dot: "bg-red-600", adminCanSet: true },
  { value: "login", label: "Login", bg: "bg-purple-50", text: "text-purple-600", dot: "bg-purple-600", adminCanSet: true },
  { value: "logout", label: "Logout", bg: "bg-yellow-50", text: "text-yellow-400", dot: "bg-yellow-400", adminCanSet: true },
];

const getStatus = (val) => {
  const status = STATUS_OPTIONS.find((option) => option.value === val);

  if (status) {
    return status;
  }

  return STATUS_OPTIONS.find((option) => option.value === "logout");
};

const StatusSelect = ({ value, agentId, onChange }) => {
  const s = getStatus(value);
  return (
    <div className={`inline-flex items-center gap-1.5 rounded-full px-2 py-1 border ${s.bg} ${s.text} border-transparent`}>
      <span className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${s.dot}`} />
      <select
        value={s.value}
        onChange={(e) => onChange(agentId, s.value, e.target.value)}
        className={`bg-transparent text-xs font-semibold border-none outline-none focus:ring-0 cursor-pointer py-0 pl-0 ${s.text}`}
      >
        {/* Keep non-settable statuses visible if agent is currently in them */}
        {!s.adminCanSet && (
          <option value={s.value} disabled>{s.label}</option>
        )}
        {STATUS_OPTIONS.filter((opt) => opt.adminCanSet).map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  );
};


// ─────────────────────────────────────────────────────────────────────────────


const INIT_PAGINATION = { next: "", previous: "", count: "", total_pages: "", current_page: 1, page_size: 5 };

const Agents = () => {
  const { auth } = useAuth();
  const fetchData = useFetch();
  const isAdmin = auth?.user_details?.type === "admin";
  const THEAD = ["Sl-No", "Agent", "Agent Status", "Virtual ID", "Vsip Password", "DID", "Call Mode", "Call Type", "Current Campaign", ... (isAdmin ? ["Action"] : [])];

  const [agents, setAgents] = useState([]);
  const axiosPrivate = UseAxiosPrivate();
  const navigate = useNavigate();
  const location = useLocation();
  const [showStatusList, setShowStatusList] = useState(false);
  const user_id = auth?.user_details?.id;

  const [pagination, setPagination] = useState(INIT_PAGINATION);

  const searchText = useRef("");
  const statusFilter = useRef(null);
  const sortFilter = useRef(null);
  const statusListFilter = useRef(null);
  const abortRef = useRef(null);

  const didFilter = useRef("");

  const [dids, setDid] = useState([]);
  const getDids = async () => {
    if (!user_id) return;

    try {
      const response = await axiosPrivate.get(
        "/contactcentre/get_did/" + user_id + "/",
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${auth?.accessToken}`,
          },
          withCredentials: true,
        }
      );

      const didList = Array.isArray(response.data)
        ? response.data
        : response.data?.dids || response.data?.results || [];

      setDid(didList);
    } catch (err) {
      console.error("Failed to load DID list", err);
    }
  };

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const getAgents = async (page) => {

    const currentStatusList = statusListFilter.current
      ? Array.from(statusListFilter.current.children).map((li) => li.dataset.value)
      : [];

    abortRef.current?.abort();
    abortRef.current = new AbortController();

    try {
      const { data } = await axiosPrivate.get(`${API}/contactcentre/agents/`, {
        params: {
          user_id: auth?.user_details?.id,
          page,
          search: searchText.current?.value ?? "",
          current_status: statusFilter.current?.value ?? "",
          current_status_list: [...selectedColumns],
          sortby: sortFilter.current?.value ?? "",
          did: didFilter.current?.value || "",
        },
        signal: abortRef.current.signal,
      });

      setAgents(data.results);
      setPagination((prev) => ({
        ...prev,
        total_pages: data?.total_pages ?? 0,
        previous: data?.previous ?? null,
        next: data?.next ?? null,
        current_page: data?.current_page ?? 0,
        count: data?.count ?? 0,
        page_size: data?.page_size ?? 5,
      }));
    } catch (err) {
      if (!axios.isCancel(err)) {
        console.error(err);
        navigate("/login", { state: { from: location }, replace: true });
      }
    }
  };

  useEffect(() => {
    getDids();
    getAgents(1);
    return () => abortRef.current?.abort();
  }, []);

  // ── Pagination ─────────────────────────────────────────────────────────────
  const PaginationControl = (type, number) => {
    const { current_page, total_pages } = pagination;
    if (type === "page") getAgents(number <= total_pages ? number : 1);
    else if (type === "last") getAgents(total_pages);
    else if (type === "prev" && current_page > 1) getAgents(current_page - 1);
    else if (type === "next" && current_page < total_pages) getAgents(current_page + 1);
    else getAgents(1);
  };

  const updateAgentStatusLocally = (agentId, status) => {
    const update = (list) =>
      list.map((a) => (a.id === agentId ? { ...a, current_active_status: status } : a));
    setAgents(update);
  };

  // ── Status change ──────────────────────────────────────────────────────────
  const handleStatusChange = async (agentId, oldStatus, newStatus) => {
    if (oldStatus === newStatus) return;

    const result = await Swal.fire({
      title: "Change Agent Status?",
      html: `Change status from <strong>${getStatus(oldStatus).label}</strong> to <strong>${getStatus(newStatus).label}</strong>?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, change it",
    });
    // Select is controlled, so on cancel it stays on the current status
    if (!result.isConfirmed) return;

    updateAgentStatusLocally(agentId, newStatus);

    try {
      const response = await axiosPrivate.post(AGENT_ACTIVITY, {
        agent_id: agentId,
        status: newStatus,
        user_id: auth?.user_details?.id,
        usertype: "admin",
      });
      if (response.data?.status !== "success") {
        throw new Error(response.data?.message || "Status update failed");
      }
      Swal.fire({ icon: "success", title: "Status updated", timer: 1500, showConfirmButton: false });
    } catch (err) {
      console.error("Status update failed", err);
      updateAgentStatusLocally(agentId, oldStatus);
      Swal.fire({ icon: "error", title: "Failed to update status", text: err?.message, confirmButtonColor: "#3085d6" });
    }
  };


  // ── Delete ─────────────────────────────────────────────────────────────────
  const AgentDelete = async (agent_id) => {
    const result = await Swal.fire({
      title: "Are you sure to Delete?",
      text: "This Agent will be deleted from your campaign",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Delete",
    });
    if (!result.isConfirmed) return;

    setAgents((prev) => prev.filter((a) => a.id !== agent_id));
    try {
      const res_data = await fetchData(
        "post",
        { user_id: auth?.user_details?.user, agent_id },
        "/contactcentre/delete_agent/"
      );

      console.log("=camp list loaded=", res_data);

      if (res_data?.status !== "success") {
        throw new Error(res_data?.message || "Failed to delete agent");
      }
    } catch (err) {
      console.error(err);
    }
  };

  // ── Serial number helper ───────────────────────────────────────────────────
  const serial = (index) => {
    const n = index + 1 + (pagination.current_page - 1) * pagination.page_size;
    return String(n).padStart(2, "0");
  };

  // const handleTextArea=(e)=>{
  //   const slf = statusListFilter.current.value;
  //   statusListFilter.current.value += `${statusFilter.current.value},`
  //   // alert(statusListFilter.current.value);
  //   array.forEach(element => {

  //   });
  // }
  // const handleTextArea = (e) => {
  //     const select = statusFilter.current;
  //     const statusList = statusListFilter.current;
  //     const value = select?.value;

  //     if (!select || !statusList || !value) return;

  //     const updateStatusListVisibility = () => {
  //         setShowStatusList(statusList.children.length > 0);
  //     };

  //     // Prevent duplicates
  //     const exists = Array.from(statusList.children).some((li) => li.dataset.value === value);

  //     if (exists) return;

  //     const li = document.createElement("li");
  //     li.dataset.value = value;
  //     li.style.cssText = `
  //         display:flex;
  //         align-items:center;
  //         gap:8px;
  //         padding:4px 10px;
  //         background:#99bbff;
  //         border:1px solid #000;
  //         border-radius:8px;
  //         color:#000;
  //         list-style:none;
  //     `;

  //     const text = document.createElement("span");
  //     text.textContent = value;

  //     const close = document.createElement("span");
  //     close.textContent = "✕";
  //     close.style.cssText = `
  //         cursor:pointer;
  //         font-weight:bold;
  //         padding:0 4px;
  //     `;

  //     close.onclick = () => {
  //         // Show the option again in select
  //         Array.from(statusFilter.current.options).forEach((option) => {
  //             if (option.value === value) {
  //                 option.hidden = false;
  //             }
  //         });

  //         li.remove();
  //         updateStatusListVisibility();
  //     };

  //     li.appendChild(text);
  //     li.appendChild(close);

  //     statusListFilter.current.appendChild(li);

  //     // Hide selected option in select
  //     Array.from(statusFilter.current.options).forEach((option) => {
  //         if (option.value === value) {
  //             option.hidden = true;
  //         }
  //     });

  //     // Show list since we added an item
  //     updateStatusListVisibility();

  //     // Reset select
  //     e.target.value = "";
  // };

  // ─────────────────────────────────────────────────────────────────────────

  const EXCEL_COLUMNS = [
    { key: "available", label: "available", default: false },
    { key: "incall", label: "incall", default: false },
    { key: "waiting", label: "waiting", default: false },
    { key: "break", label: "break", default: false },
    { key: "unavailable", label: "unavailable", default: false },
    { key: "login", label: "login", default: false },
    { key: "logout", label: "logout", default: false }
  ];

  const [selectedColumns, setSelectedColumns] = useState(
    () => new Set(EXCEL_COLUMNS.filter((c) => c.default).map((c) => c.key))
  );
  const [showColumnPicker, setShowColumnPicker] = useState(false);

  const toggleColumn = (key) => {
    setSelectedColumns((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const toggleAllColumns = () => {
    if (selectedColumns.size === EXCEL_COLUMNS.length) {
      setSelectedColumns(new Set());
    } else {
      setSelectedColumns(new Set(EXCEL_COLUMNS.map((c) => c.key)));
    }
  };


  return (
    <div className="flex flex-col bg-white">
      {/* ── Toolbar ── */}
      <div className="flex flex-wrap items-center gap-2 p-3">
        {/* Search */}
        <div className="relative">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
          </div>
          <input
            ref={searchText}
            type="text"
            placeholder="Search agents"
            className="pl-9 pr-3 py-2 text-sm bg-gray-50 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 w-48 sm:w-64"
          />
        </div>

        <div className="relative ml-3 mt-0">
          <button
            className="flex items-center gap-2 bg-blue-50 border border-blue-300 text-blue-700 text-sm font-medium px-0 py-2 rounded-lg hover:bg-blue-100 transition mr-3 max-[680px]:w-[6.5rem] max-[680px]:pr-[0rem] max-[890px]:pr-0 max-[890px]:w-[8.5rem] max-[1175px]:w-[7rem] max-[1320px]:w-[12rem] max-[1320px]:pl-1 max-[1560px]:w-60 w-80 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block pl-10 p-2.5 mt-0"
            onClick={() => setShowColumnPicker((p) => !p)}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round"
                d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7" />
            </svg>
            Select Agent Status
            <span className="bg-blue-200 text-blue-800 text-xs rounded-full px-2 py-0.5">
              {selectedColumns.size}/{EXCEL_COLUMNS.length}
            </span>
          </button>

          {showColumnPicker && (
            <div className="absolute z-20 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl p-4 w-72">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-semibold text-gray-700">Select Columns</h3>
                <button
                  className="text-xs text-blue-600 hover:underline"
                  onClick={toggleAllColumns}
                >
                  {selectedColumns.size === EXCEL_COLUMNS.length ? "Deselect All" : "Select All"}
                </button>
              </div>

              <div className="flex flex-col gap-2 max-h-64 overflow-y-auto">
                {EXCEL_COLUMNS.map((col) => (
                  <label
                    key={col.key}
                    className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-50 px-1 py-0.5 rounded"
                  >
                    <input
                      type="checkbox"
                      className="accent-blue-500"
                      checked={selectedColumns.has(col.key)}
                      onChange={() => toggleColumn(col.key)}
                    />
                    {col.label}
                  </label>
                ))}
              </div>

              <div className="mt-3 pt-2 border-t flex justify-end">
                <button
                  className="text-sm bg-blue-500 hover:bg-blue-600 text-white px-4 py-1.5 rounded-lg"
                  onClick={() => setShowColumnPicker(false)}
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>


        {/* <div className="relative inline-block">
                <select
                    className="mr-5 max-[680px]:w-[6.5rem] max-[680px]:pr-[0rem] max-[890px]:pr-0 max-[890px]:w-[8.5rem] max-[1175px]:w-[7rem] max-[1320px]:w-[12rem] max-[1320px]:pl-1 max-[1560px]:w-60 w-80 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block pl-10 p-2.5 mt-1 me-5"
                    ref={statusFilter}
                    defaultValue=""
                    onChange={handleTextArea}
                    onFocus={() => {
                        if (statusListFilter.current?.children.length > 0) {
                            setShowStatusList(true);
                        }
                    }}
                >
                    <option value="" disabled>Select Agent Status</option>
                    <option value={"available"}>Available</option>
                    <option value={"incall"}>Incall</option>
                    <option value={"waiting"}>Waiting</option>
                    <option value={"break"}>Break</option>
                    <option value={"unavailable"}>Unavailable</option>
                    <option value={"login"}>Login</option>
                    <option value={"logout"}>Logout</option>
                </select>

                <div
                    className={`absolute top-full left-0 mt-2 border rounded-lg bg-white ${
                        showStatusList ? "" : "hidden"
                    }`}
                >
                    <button
                        type="button"
                        onClick={() => setShowStatusList(false)}
                        className="absolute top-0 right-1 text-gray-500 hover:text-black"
                    >
                        ✕
                    </button>

                    <ul
                        ref={statusListFilter}
                        className="flex flex-row flex-wrap items-center gap-2 p-2 pr-8 text-sm"
                    />
                </div>
            </div> */}

        {/* Sort */}
        <select ref={sortFilter}
          className="py-2 px-3 text-sm bg-gray-50 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 w-44 sm:w-52">
          <option value="">Sort by</option>
          <option value="by_name">By Agent Name</option>
          <option value="by_created">By Creation Time</option>
        </select>
        <div className="relative ml-0 mb-0">
          <select
            className="max-[680px]:w-[6.5rem] max-[680px]:pr-[0rem] max-[890px]:w-[8.5rem] max-[1175px]:pr-0 max-[1175px]:w-[8.5rem] max-[1320px]:w-[12rem] max-[1320px]:pl-1 max-[1560px]:w-60 w-80 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block pl-10 p-2.5 mt-1 mr-5"
            ref={didFilter}
            defaultValue=""
          >
            <option value="">All DIDs</option>
            {dids.map((didItem, index) => {
              const didValue =
                typeof didItem === "string"
                  ? didItem
                  : didItem?.did || didItem?.number || didItem?.value || "";

              if (!didValue) return null;

              return (
                <option key={`${didValue}-${index}`} value={didValue}>
                  {didValue}
                </option>
              );
            })}
          </select>
        </div>

        <button
          onClick={() => getAgents(1)}
          className="py-2 px-4 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors">
          Search
        </button>
        {isAdmin && (
        <Link to="/admin/agent/create/0" className="ml-auto">
          <button className="py-2 px-4 text-sm font-medium text-white bg-green-500 hover:bg-green-600 rounded-lg transition-colors uppercase tracking-wide">
            + Add Agent
          </button>
        </Link>
        )}
      </div>

      {/* ── Table ── */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-gray-400 font-medium border-b">
              {THEAD.map((h) => (
                <th key={h} className="px-6 py-3 text-left font-medium whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {agents.map((agent, index) => (
              <tr key={agent.id} className="border-b hover:bg-gray-50 transition-colors text-gray-800 whitespace-nowrap">
                <td className="px-6 py-4 font-mono text-sm text-gray-500">{serial(index)}</td>
                <td className="px-6 py-4">
                  <span className="font-medium">{agent.profile_name}</span>
                  <br />
                  <span className="text-xs text-gray-400">{agent.phone_number}</span>
                </td>
                <td className="pl-[2.1rem] pr-[1.5rem]  py-4">
                  <StatusSelect
                    value={agent.current_active_status}
                    agentId={agent.id}
                    onChange={handleStatusChange}
                  />
                </td>
                <td className="px-6 py-4">{agent.vsip_id || "-"}</td>
                <td className="px-6 py-4">{agent.vsip_password || "-"}</td>
                <td className="px-6 py-4">{agent.user_did || "-"}</td>
                <td className="px-6 py-4">{agent.call_mode || "-"}</td>
                <td className="px-6 py-4">{agent.call_options || "-"}</td>
                <td className="px-6 py-4">{agent.current_campaign || "-"}</td>
                {isAdmin && (
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <Link to={`/admin/agent/create/${agent.id}`}>
                      <button className="p-1.5 rounded hover:bg-gray-100 transition-colors">
                        <Edit className="w-5 h-5" fill="green" viewBox="0 0 26 26" />
                      </button>
                    </Link>
                    <button
                      onClick={() => AgentDelete(agent.id)}
                      className="p-1.5 rounded hover:bg-gray-100 transition-colors">
                      <Delete className="w-5 h-5" fill="red" viewBox="0 0 26 26" />
                    </button>
                  </div>
                </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="p-6">
        <Pagination pagination_data={pagination} PaginationControl={PaginationControl} />
      </div>
    </div>
  );
};

export default Agents;